import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithPopup,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
  reload
} from 'firebase/auth';
import { auth, googleProvider } from '@/lib/firebase';
import { addActivityLog } from '@/lib/activityLog';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (displayName: string, photoURL?: string) => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function getVerificationActionUrl() {
  if (typeof window === 'undefined') return 'https://applywisely.in/verify-email';
  return `${window.location.origin}/verify-email`;
}

async function sendVerificationEmailSafe(user: User) {
  try {
    await sendEmailVerification(user, {
      url: getVerificationActionUrl(),
      handleCodeInApp: false,
    });
  } catch (error) {
    // Fallback for environments where continue URL config is restrictive.
    await sendEmailVerification(user);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const result = await signInWithEmailAndPassword(auth, email, password);

    // Check if email is verified
    if (!result.user.emailVerified) {
      // Send verification email if not verified
      await sendVerificationEmailSafe(result.user);
      throw { code: 'auth/email-not-verified', message: 'Please verify your email first.' };
    }

    try {
      await addActivityLog(result.user.uid, 'login', `Signed in with email ${email}`);
    } catch (err) {
      console.warn('Activity log write failed during sign-in:', err);
    }
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, { displayName });

    // Send verification email
    await sendVerificationEmailSafe(result.user);

    try {
      await addActivityLog(result.user.uid, 'login', `Created account as ${displayName}. Verification email sent.`);
    } catch (err) {
      console.warn('Activity log write failed during sign-up:', err);
    }
  };

  const signInWithGoogle = async () => {
    // Use popup method which is more reliable and doesn't require authDomain hosting setup
    const result = await signInWithPopup(auth, googleProvider);
    try {
      await addActivityLog(result.user.uid, 'login', `Signed in with Google as ${result.user.displayName || result.user.email}`);
    } catch (err) {
      console.warn('Activity log write failed during Google sign-in:', err);
    }
  };

  const logout = async () => {
    if (auth.currentUser) {
      try {
        await addActivityLog(auth.currentUser.uid, 'logout', 'Signed out');
      } catch (err) {
        console.warn('Activity log write failed during logout:', err);
      }
    }
    await signOut(auth);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const updateUserProfile = async (displayName: string, photoURL?: string) => {
    if (auth.currentUser) {
      await updateProfile(auth.currentUser, { displayName, photoURL });
      // Force refresh user state
      setUser({ ...auth.currentUser });
    }
  };

  const resendVerificationEmail = async () => {
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      await sendVerificationEmailSafe(auth.currentUser);
    }
  };

  const refreshUser = async () => {
    if (auth.currentUser) {
      await reload(auth.currentUser);
      setUser({ ...auth.currentUser });
    }
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    logout,
    resetPassword,
    updateUserProfile,
    resendVerificationEmail,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
